import { useCallback } from "react";
import { find, isEmpty, isEqual, map, pick } from "lodash";
import { useFieldArray, useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { accessGroupPaths } from "@circle-react/helpers/urlHelpers/accessGroupPaths";
import type {
  AccessGroupSourceType,
  AccessGroupType,
  SelectedAccessGroupType,
} from "@circle-react/types/AccessGroup";
import {
  activeCount,
  normalizeAccessGroupSources,
  originalAccessGroupSourcesNormalized,
} from "@circle-react-shared/AccessGroups/helpers";
import { useToast } from "@circle-react-uikit/ToastV2";
import { useAccessGroupSourcesData } from "../DataStoreContext/AccessGroupSourcesDataContext";
import { ACTION_STATUSES } from "../constants";
import { useOpenNewAccessGroupModal } from "./useOpenNewAccessGroupModal";

const ACCESS_GROUP_FORM_KEY = "access_groups";
const ACCESS_GROUP_SOURCES_FORM_KEY = "access_group_sources";
export const useAccessGroupsActions = () => {
  const toast = useToast();
  const {
    originalAccessGroupSources,
    canRemoveAllAccessGroups,
    recordId,
    isSyncing,
    syncingTooltip,
  } = useAccessGroupSourcesData();

  const areAccessGroupsSetFromFormDefaults = !recordId;

  const { watch } = useFormContext();
  const { replace, remove, append, update } = useFieldArray({
    name: ACCESS_GROUP_FORM_KEY,
  });

  const selectedAccessGroups: Array<SelectedAccessGroupType> = watch(
    ACCESS_GROUP_FORM_KEY,
  );

  const originalAccessGroupSourcesFromForm: Array<AccessGroupSourceType> =
    watch(ACCESS_GROUP_SOURCES_FORM_KEY);

  const finalOriginalAccessGroupSources = isEmpty(originalAccessGroupSources)
    ? originalAccessGroupSourcesFromForm
    : originalAccessGroupSources;

  const isFormDirty = useCallback(() => {
    const originalItems = originalAccessGroupSourcesNormalized(
      finalOriginalAccessGroupSources,
    );

    const selectedItems = map(selectedAccessGroups, function (obj) {
      return pick(obj, ["id", "status"]);
    });
    return !isEqual(originalItems, selectedItems);
  }, [finalOriginalAccessGroupSources, selectedAccessGroups]);

  const findAccessGroupIndex = useCallback(
    (accessGroupId: number) =>
      selectedAccessGroups.findIndex(
        accessGroup => accessGroup.id === accessGroupId,
      ),
    [selectedAccessGroups],
  );

  const canRemoveAccessGroups = useCallback(() => {
    if (canRemoveAllAccessGroups) {
      return true;
    }
    return activeCount(selectedAccessGroups).length > 1;
  }, [canRemoveAllAccessGroups, selectedAccessGroups]);

  const removeAccessGroup = useCallback(
    (accessGroup: SelectedAccessGroupType) => {
      const index = findAccessGroupIndex(accessGroup.id);
      if (!canRemoveAccessGroups()) {
        return toast.error(
          t(
            "settings.access_groups.admin.entry_points.association.messages.cannot_remove_access_group",
          ),
        );
      }
      if (accessGroup.hasMembers) {
        update(index, {
          ...selectedAccessGroups[index],
          status: ACTION_STATUSES.REMOVED,
        });
      } else {
        remove(index);
      }
    },
    [
      findAccessGroupIndex,
      update,
      selectedAccessGroups,
      remove,
      canRemoveAccessGroups,
      toast,
    ],
  );

  const isAccessGroupSourceOriginallyAdded = (accessGroupId: number) =>
    Boolean(
      find(
        finalOriginalAccessGroupSources,
        source => source.access_group.id === accessGroupId,
      ),
    );

  const appendAccessGroup = (accessGroup: AccessGroupType) => {
    append({
      ...accessGroup,
      hasMembers: false,
      status: isAccessGroupSourceOriginallyAdded(accessGroup.id)
        ? ACTION_STATUSES.ACTIVE
        : ACTION_STATUSES.NEW,
    });
  };

  const { openNewAccessGroupModal } = useOpenNewAccessGroupModal({
    selectNewAccessGroup: appendAccessGroup,
  });

  const getOriginalAccessGroup = (accessGroupId: number) =>
    find(
      finalOriginalAccessGroupSources,
      source => source.access_group.id === accessGroupId,
    );

  const isAccessGroupSourceRemoved = (status: string) =>
    status === ACTION_STATUSES.REMOVED;

  const isAcessGroupSourceActive = (status: string) =>
    status === ACTION_STATUSES.ACTIVE;

  const reEnableAccessGroup = (accessGroupId: number) => {
    const originalAccessGroup = getOriginalAccessGroup(accessGroupId);
    const index = findAccessGroupIndex(accessGroupId);
    update(index, {
      ...selectedAccessGroups[index],
      status: originalAccessGroup?.deleted_at
        ? ACTION_STATUSES.RE_ENABLE
        : ACTION_STATUSES.ACTIVE,
    });
  };

  const updateAccessGroupSourcesDefaults = useCallback(
    (newAccessGroupSources: AccessGroupSourceType[]) => {
      replace(normalizeAccessGroupSources(newAccessGroupSources));
    },
    [replace],
  );

  const redirectToAccessGroups = (accessGroupId: number) => {
    window.open(
      accessGroupPaths.edit({
        accessGroupId: accessGroupId,
      }),
      "_blank",
    );
  };

  const shouldShowRemoveConfirmationModal = (
    accessGroupSource: SelectedAccessGroupType,
  ) =>
    Boolean(
      isAcessGroupSourceActive(accessGroupSource.status) &&
        accessGroupSource.hasMembers,
    );

  return {
    appendAccessGroup,
    removeAccessGroup,
    selectedAccessGroups,
    openNewAccessGroupModal,
    isAccessGroupSourceRemoved,
    reEnableAccessGroup,
    updateAccessGroupSourcesDefaults,
    redirectToAccessGroups,
    isAcessGroupSourceActive,
    shouldShowRemoveConfirmationModal,
    finalOriginalAccessGroupSources,
    isFormDirty: isFormDirty(),
    areAccessGroupsSetFromFormDefaults,
    isSyncing,
    syncingTooltip,
  };
};
